import { useMutation, useQuery } from "@apollo/client";
import { Add, Delete, Edit, Visibility, VisibilityOff } from "@mui/icons-material";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Input,
    InputAdornment,
    InputLabel,
    Paper,
    styled,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Error from "../common/Error";
import Loading from "../common/Loading";
import { pages } from "../pages/pages";
import { Exercise } from "../types";
import { deleteImage } from "../utils/deleteImage";
import { getExercisesByCategory } from "../utils/exercises";
import { deleteExerciseMutation, exercisesTableQuery, ExercisesTableQueryType } from "./Exercise.gql";

const ExercisesTable: React.FunctionComponent = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [dialogOpen, setDialogOpen] = React.useState(sessionStorage.getItem("password") ? false : true);
    const [deleteMutation, { loading: deleteLoading }] = useMutation(deleteExerciseMutation);
    const { data, loading } = useQuery<ExercisesTableQueryType>(exercisesTableQuery, { fetchPolicy: "network-only" });
    const [fieldValue, setFieldValue] = React.useState("");
    const [showPassword, setShowPassword] = React.useState(false);

    const handleDelete = async (id: number, url: string) => {
        await deleteImage(url);
        await deleteMutation({
            variables: {
                id,
            },
            refetchQueries: ["GetExercises"],
        });
    };

    const handleEnter = () => {
        if (fieldValue === "ExercisingIsFun") {
            setDialogOpen(false);
            sessionStorage.setItem("password", fieldValue);
        } else {
            navigate(pages.home);
        }
    };

    const handleShowPassword = () => {
        setShowPassword(true);
    };

    const handleHidePassword = () => {
        setShowPassword;
    };

    const exerciseCategories = getExercisesByCategory(data?.exercisesTable ?? []);
    const existingCategories = Object.keys(exerciseCategories) as Array<keyof typeof exerciseCategories>;

    return (
        <Container>
            <Dialog open={dialogOpen}>
                <DialogTitle>{t("password")}</DialogTitle>
                <DialogContent style={{ padding: 20 }}>
                    <DialogContentText style={{ marginBottom: 20 }}>{t("insert_password")}</DialogContentText>
                    <InputLabel>{t("password")}</InputLabel>
                    <Input
                        autoFocus
                        margin="dense"
                        value={fieldValue}
                        type={showPassword ? "text" : "password"}
                        fullWidth
                        onChange={(e) => {
                            setFieldValue(e.target.value);
                        }}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton onClick={handleShowPassword} onMouseDown={handleHidePassword} edge="end">
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            navigate(pages.home);
                        }}
                    >
                        {t("cancel")}
                    </Button>
                    <Button onClick={handleEnter}>Ok</Button>
                </DialogActions>
            </Dialog>
            <div>
                <Button startIcon={<Add />} variant="contained" onClick={() => navigate(`/exercises/add`)}>
                    {t("add_exercise")}
                </Button>
            </div>
            <TableContainer component={Paper} style={{ marginTop: 20 }}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>{t("category")}</TableCell>
                            <TableCell>{t("type")}</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading || deleteLoading ? (
                            <Loading />
                        ) : data ? (
                            data.exercisesTable?.map((exercise: Exercise) => (
                                <TableRow key={exercise.id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                    <TableCell component="th" scope="row">
                                        {i18n.language === "de" ? exercise.nameDe : exercise.nameEn}
                                    </TableCell>
                                    <TableCell>{t(exercise.category)}</TableCell>
                                    <TableCell>{exercise.discreet ? t("inconspicuous") : t("conspicuous")}</TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={() => navigate(`/exercises/${exercise.id}`, { state: exercise.id })}>
                                            <Edit />
                                        </IconButton>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={() => handleDelete(exercise.id, exercise.image)}>{<Delete />}</IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <Error text={t("error")} />
                        )}
                    </TableBody>
                    <TableFooter>
                        <Typography style={{ padding: 20 }}>
                            {data?.exercisesTable?.length ?? 0} {t("exercises")}
                        </Typography>
                        <Typography style={{ padding: 20 }}>
                            {existingCategories.map((category, index) => (
                                <div key={index}>
                                    <Typography>
                                        {exerciseCategories[category]?.length} {t(category)}
                                    </Typography>
                                    <Typography style={{ marginLeft: 10 }}>
                                        Auffällig: {exerciseCategories[category]?.filter((exercise) => !exercise.discreet).length}
                                    </Typography>
                                    <Typography style={{ marginLeft: 10 }}>
                                        Unauffällig: {exerciseCategories[category]?.filter((exercise) => exercise.discreet).length}
                                    </Typography>
                                </div>
                            ))}
                        </Typography>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Container>
    );
};

export default ExercisesTable;

const Container = styled("div")`
    padding: 20px;
    align-items: flex-end;
    display: flex;
    flex-direction: column;
`;
